import {
  Component,
  computed,
  inject,
  Injector,
  model,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { get } from 'lodash';
import { startWith } from 'rxjs';
import { DataTableComponent } from '../data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '../data-table/data-table.types';
import { DataTableAssignConfiguration } from './data-table-assign.types';

@Component({
  selector: 'app-data-table-assign',
  imports: [
    DataTableComponent,
    NgbTooltip,
    TranslateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './data-table-assign.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './data-table-assign.component.scss',
})
export class DataTableAssignComponent implements ControlValueAccessor, OnInit {
  private ngControl = inject(NgControl, { self: true, optional: true });
  private injector = inject(Injector);

  public configuration = model<DataTableAssignConfiguration>();

  protected leftTableConfiguration: DataTableConfiguration;
  protected rightTableConfiguration: DataTableConfiguration;
  protected searchControl = new FormControl('', { nonNullable: true });
  protected searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
    { initialValue: this.searchControl.value }
  );
  protected filteredLeftRows = computed(() => {
    const rows = this.configuration().leftTable.rows();
    const search = this.configuration().leftTable.search;
    const normalizedSearch = this.searchTerm().trim().toLowerCase();

    if (!search || !normalizedSearch) {
      return rows;
    }

    return rows.filter((row) =>
      String(get(row, search.property) ?? '')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  });

  private onChange: any = () => {};
  private onTouched: any = () => {};

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.leftTableConfiguration = {
      title: this.configuration().leftTable.title,
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'shared.dataTableAssign.actions.add',
          action: this.add.bind(this),
          icon: 'fa-light fa-plus',
          type: DataTableActionType.BUTTON,
          permission: this.configuration().leftTable.buttonPermission,
        },
      ],
      columns: [
        {
          label: this.configuration().columnName,
          propertyTemplate: this.configuration().leftTable.columnTemplate,
        },
      ],
      rows$: toObservable(this.filteredLeftRows, {
        injector: this.injector,
      }),
    };

    this.rightTableConfiguration = {
      title: this.configuration().rightTable.title,
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'shared.dataTableAssign.actions.remove',
          action: this.remove.bind(this),
          icon: 'fa-light fa-xmark',
          type: DataTableActionType.BUTTON,
          permission: this.configuration().rightTable.buttonPermission,
        },
      ],
      columns: [
        {
          label: this.configuration().columnName,
          propertyTemplate: this.configuration().rightTable.columnTemplate,
        },
      ],
      rows$: toObservable(this.configuration().rightTable.rows, {
        injector: this.injector,
      }),
    };
  }

  protected addAll(event: MouseEvent) {
    event.preventDefault();

    const visibleRows = this.filteredLeftRows();
    if (!visibleRows.length) {
      return;
    }

    this.configuration().leftTable.rows.update((rows) =>
      rows.filter((row) => !visibleRows.includes(row))
    );
    this.configuration().rightTable.rows.update((rows) => [
      ...rows,
      ...visibleRows,
    ]);
    this.onTouched();
    this.onChange(this.configuration().rightTable.rows());
  }

  protected add(event: MouseEvent, row: any) {
    event.preventDefault();

    this.configuration().leftTable.rows.update((rows) =>
      rows.filter((r) => r !== row)
    );
    this.configuration().rightTable.rows.update((rows) => [...rows, row]);
    this.onTouched();
    this.onChange(this.configuration().rightTable.rows());
  }

  protected remove(event: MouseEvent, row: any) {
    event.preventDefault();

    this.configuration().rightTable.rows.update((rows) =>
      rows.filter((r) => r !== row)
    );
    this.configuration().leftTable.rows.update((rows) => [...rows, row]);
    this.onTouched();
    this.onChange(this.configuration().rightTable.rows());
  }

  writeValue(obj: any): void {
    const selectedRows = Array.isArray(obj) ? obj : [];
    const allRows = [
      ...this.configuration().leftTable.rows(),
      ...this.configuration().rightTable.rows(),
    ];

    this.configuration().rightTable.rows.set(selectedRows);
    this.configuration().leftTable.rows.set(
      allRows.filter(
        (row) => !selectedRows.some((selectedRow) => row === selectedRow)
      )
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}
}
